import Assignment from '@mui/icons-material/AssignmentOutlined';
import MessageOutlined from '@mui/icons-material/MessageOutlined';
import OpenInNew from '@mui/icons-material/OpenInNew';
import RequestQuoteIcon from '@mui/icons-material/RequestQuoteOutlined';
import ShoppingBasketOutlinedIcon from '@mui/icons-material/ShoppingBasketOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarIconOutline from '@mui/icons-material/StarOutline';
import { Box, Checkbox, IconButton, Tooltip, Typography } from '@mui/material';
import ButtonWithTooltip from 'components/common/ContactButton/ButtonWithTooltip';
import { COMPARE_BULK_LIMIT } from 'components/common/Search/useSelection';
import TooltipContent from 'components/common/TooltipContent';
import { getToolTip } from 'components/pages/product/variants/UXB/Sections/Requests/helperFunctions';
import { isProductOutOfStock } from 'components/pages/products/helpers/formAttributes';
import {
  ActionsModalType,
  ProductActionModals,
  useSaveClickEvent
} from 'components/pages/projects/helpers/ProductActions/ProductActionModals';
import { SearchContext } from 'context/SearchContext';
import { useIfVerifiedUser } from 'hooks/useUserState_utils';
import { MouseEvent, Suspense, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { Link } from 'react-router-dom';
import { UXB_EVENT_KEY } from 'supplier-portals/uxbeauty/constants';
import { ContactRequestContext, RequestTypes } from 'types/ContactRequests';
import { LogEventType } from 'types/LogEvents';
import { ProductType } from 'types/Products';
import { EventSearchDocument } from 'types/Search';

interface RowProps {
  product: { document: EventSearchDocument };
  logItemClick: Function;
  classes: {
    columns: string;
    header: string;
  };
  columns: {
    name: boolean;
    description: boolean;
    actions: boolean;
  };
  selected: boolean;
  onSelectedChange: (selected: boolean) => void;
  bookmarked: boolean;
  onBookmarkChange: (bookmarked: boolean) => void;
  selectionModeEnabled: boolean; // maybe in context
  canSelectMore: boolean; // maybe in context
}

const Row = ({ product, classes, columns, ...props }: RowProps) => {
  const { isAuthenticated, isLoading } = useAuth();
  const verified = useIfVerifiedUser();
  const notAuthenticated = isLoading || isAuthenticated === false;
  const { t } = useTranslation();

  const { request } = useContext(SearchContext);

  const [bookmarked, setBookmarked] = useState(props.bookmarked);
  const saveClickEvent = useSaveClickEvent();
  useEffect(() => {
    setBookmarked(props.bookmarked);
  }, [props.bookmarked]);

  function rowClicked(event: MouseEvent, activeRow: boolean) {
    const isValidCompareMode =
      (props.selectionModeEnabled && props.canSelectMore) || activeRow;

    if (isValidCompareMode) {
      event.preventDefault();
      props.onSelectedChange(!props.selected);
    }
  }

  const logEventsData = {
    products: [product.document],
    searchRequest: request
  };

  const handleRequestModal = (
    type: ActionsModalType,
    eventLog: LogEventType | null
  ) => {
    if (eventLog !== null)
      saveClickEvent(logEventsData, eventLog, product.document.segment);

    setModalType(type);
  };

  const [modalType, setModalType] = useState<ActionsModalType>();

  const isOutOfStock = isProductOutOfStock(
    product.document.event?.product_private_attributes?.data?.inStock?.[0]
  );
  return (
    <Link
      className={classes.columns}
      onClick={e => rowClicked(e, props.selected)}
      to={`/ingredients/permalink/${product?.document.old_key}`}
    >
      {props.selectionModeEnabled ? (
        <TooltipContent
          title={`You can just compare up to ${COMPARE_BULK_LIMIT} products`}
          showTooltip={!props.canSelectMore}
        >
          <Checkbox
            sx={{ p: 0 }}
            disableRipple
            checked={props.selected}
            onChange={(_, checked) => props.onSelectedChange(checked)}
            disabled={!props.canSelectMore && !props.selected}
          />
        </TooltipContent>
      ) : (
        <TooltipContent
          title={t(
            `search.uxbeauty.favorite.tooltips.${
              notAuthenticated ? 'disabled' : bookmarked ? 'remove' : 'add'
            }`
          )}
          showTooltip={true}
        >
          <Box
            sx={{
              '&:hover': {
                cursor: !notAuthenticated ? 'pointer' : 'not-allowed'
              }
            }}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <IconButton
              size="small"
              onClick={() => {
                if (isAuthenticated) {
                  props.onBookmarkChange(bookmarked);
                  setBookmarked(!bookmarked);
                }
              }}
              color="primary"
              disabled={notAuthenticated}
            >
              {bookmarked ? <StarIcon /> : <StarIconOutline />}
            </IconButton>
          </Box>
        </TooltipContent>
      )}
      <Typography
        variant="h5"
        sx={{
          color: 'primary'
        }}
      >
        {product.document.name}
      </Typography>
      {columns.description && (
        <Typography
          variant="body2"
          className="truncate search-result-list-info"
        >
          {product?.document.marketing?.description}
        </Typography>
      )}
      <div className="d-flex  justify-content-end text-end">
        {columns.actions && (
          <div
            onClick={e => {
              // Be careful here, adding e.prevenDefault would break the request dialog functionality!
              e.stopPropagation();
            }}
          >
            {product.document.type === ProductType.INGREDIENTS && (
              <ButtonWithTooltip
                tooltip={getToolTip(
                  t,
                  RequestTypes.QUOTE,
                  product.document.type,
                  !notAuthenticated,
                  verified
                )}
                onClick={e => {
                  e.preventDefault();
                  handleRequestModal(
                    ActionsModalType.QUOTE,
                    LogEventType.OPENED_REQUEST_QUOTE
                  );
                }}
                icon={<RequestQuoteIcon />}
                disabled={notAuthenticated || !verified}
              />
            )}

            <ButtonWithTooltip
              tooltip={getToolTip(
                t,
                RequestTypes.SAMPLE,
                product.document.type,
                !notAuthenticated,
                verified,
                isOutOfStock
              )}
              onClick={e => {
                e.preventDefault();
                handleRequestModal(
                  ActionsModalType.CART_SAMPLE,
                  LogEventType.OPENED_REQUEST_SAMPLE
                );
              }}
              icon={<ShoppingBasketOutlinedIcon />}
              disabled={notAuthenticated || !verified || isOutOfStock}
            />

            <ButtonWithTooltip
              onClick={e => {
                e.preventDefault();
                handleRequestModal(
                  ActionsModalType.DOCUMENT_REQUEST,
                  LogEventType.OPENED_REQUEST_DOCUMENT
                );
              }}
              icon={<Assignment />}
              tooltip={getToolTip(
                t,
                RequestTypes.DOCUMENT,
                product.document.type,
                !notAuthenticated,
                verified
              )}
              disabled={notAuthenticated || !verified}
            />
            <ButtonWithTooltip
              onClick={e => {
                e.preventDefault();
                handleRequestModal(
                  ActionsModalType.CONTACT,
                  product.document.type === ProductType.INGREDIENTS
                    ? LogEventType.OPENED_REQUEST_CONTACT
                    : LogEventType.OPENED_FORMULATION_REQUEST_CONTACT
                );
              }}
              icon={<MessageOutlined />}
              tooltip={getToolTip(
                t,
                RequestTypes.CONTACT,
                product.document.type,
                !notAuthenticated,
                verified
              )}
              disabled={notAuthenticated || !verified}
            />
            {modalType && product.document.event?.key && (
              <Suspense>
                <ProductActionModals
                  modalType={modalType}
                  productType={product.document.type}
                  logEventsData={logEventsData}
                  onClose={() => setModalType(undefined)}
                  context={{
                    type: ContactRequestContext.SUPPLIER_PORTAL,
                    portalKey: UXB_EVENT_KEY
                  }}
                />
              </Suspense>
            )}
          </div>
        )}
        <Tooltip title="Open product page">
          <div>
            <Link to={`/ingredients/permalink/${product?.document.old_key}`}>
              <IconButton size="small" color="primary">
                <OpenInNew color="primary" />
              </IconButton>
            </Link>
          </div>
        </Tooltip>
      </div>
    </Link>
  );
};

export default Row;
